import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Typography } from '@mui/material';

import { useT } from '@/hooks';

import { FLAGS_BASE_URL } from '@/constants';

interface DepositAmountSectionProps {
  fiatCurrency: string;
  fiatSymbol: string;
  amount: number | string;
  setAmount: (val: any) => void;
  country: string;
  onShowCountryList: () => void;
  onShowFiatList: () => void;
  quickAmounts: number[];
  cryptoCurrency: string;
  payout?: string | number;
  hasSelectedQuote: boolean;
  selectedProviderName?: string;
  selectedProviderLogo?: string;
  onShowProviderList: () => void;
  displayError?: string | null;
  isLoadingQuotes?: boolean;
  availabilityError?: boolean;
}

export const DepositAmountSection = ({
  fiatCurrency,
  fiatSymbol,
  amount,
  setAmount,
  country,
  onShowCountryList,
  onShowFiatList,
  quickAmounts,
  cryptoCurrency,
  payout,
  hasSelectedQuote,
  selectedProviderName,
  selectedProviderLogo,
  onShowProviderList,
  displayError,
  isLoadingQuotes,
  availabilityError,
}: DepositAmountSectionProps) => {
  const { t } = useT();

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val === '' || /^\d*\.?\d*$/.test(val)) {
      setAmount(val);
    }
  };

  return (
    <Box className="flex flex-col gap-2">
      {/* You Spend */}
      <Box className="flex flex-col gap-2 rounded-[24px] bg-[var(--bg-secondary)] p-6 dark:bg-[#F8F9FB10]">
        <Typography className="text-sm font-medium text-[var(--text-muted)]">
          {t('depositModal.youSpend', { defaultValue: 'You spend' })}
        </Typography>
        <Box className="flex items-center justify-between gap-3 overflow-hidden">
          <Box className="flex flex-1 items-center gap-1">
            <Typography className="text-3xl font-bold text-[var(--text-primary)] opacity-40">
              {fiatSymbol}
            </Typography>
            <input
              type="text"
              inputMode="decimal"
              className="w-full border-none bg-transparent p-0 text-3xl font-bold text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)]"
              value={amount || ''}
              onChange={handleAmountChange}
              placeholder="0"
            />
          </Box>
          <Box className="flex shrink-0 items-center gap-2">
            <button
              onClick={onShowFiatList}
              className="flex items-center gap-1 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm font-bold text-[var(--text-primary)] transition-all hover:bg-[var(--hover-bg)]"
            >
              <Box
                component="img"
                src={
                  fiatCurrency
                    ? `${FLAGS_BASE_URL}/${fiatCurrency.slice(0, 2).toLowerCase()}.svg`
                    : ''
                }
                className="h-5 w-5 object-cover"
                onError={(e: any) => (e.target.style.display = 'none')}
              />
              {fiatCurrency}
              <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'var(--text-muted)' }} />
            </button>
          </Box>
        </Box>
      </Box>

      {/* Error Message */}
      {displayError && (
        <Box className="flex items-start gap-2 px-1">
          <Box className="text-[var(--accent-red)]">⚠️</Box>
          <Typography
            sx={{
              fontSize: '12px',
              lineHeight: '20px',
              color: 'var(--accent-red)',
            }}
          >
            {displayError}
          </Typography>
        </Box>
      )}

      {/* You Get */}
      <Box className="flex flex-col gap-2 rounded-[24px] bg-[var(--bg-secondary)] p-6 dark:bg-[#F8F9FB10]">
        <Typography className="text-sm font-medium text-[var(--text-muted)]">
          {t('depositModal.youGet', { defaultValue: 'You get' })}
        </Typography>
        <Box className="flex items-center justify-between">
          {isLoadingQuotes ? (
            <Box className="h-8 w-20 animate-pulse rounded-lg bg-[var(--border-color)]" />
          ) : (
            <Typography
              className={`font-bold text-[var(--text-primary)] ${!payout && 'opacity-40'}`}
              sx={{
                fontSize: '24px',
                lineHeight: '32px',
                fontWeight: 600,
              }}
            >
              {payout
                ? Number(payout).toLocaleString(undefined, { maximumFractionDigits: 4 })
                : '0'}
            </Typography>
          )}
          <Box className="flex flex-col items-end gap-1">
            <button className="flex items-center gap-2 rounded-xl bg-[var(--bg-primary)] px-3 py-2 text-sm font-bold text-[var(--text-primary)] shadow-sm">
              <Box
                component="img"
                src="/tokens/usdc.svg"
                className="h-5 w-5"
                onError={(e: any) => (e.target.style.display = 'none')}
              />
              {(cryptoCurrency || 'USDC').split('_')[0]}
              {/* <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'var(--text-muted)' }} /> */}
            </button>
            <Box className="flex items-center gap-1.5 px-1">
              <Box className="h-2 w-2 rounded-full bg-[#627EEA]" />
              <Typography className="text-[11px] font-medium text-[var(--text-muted)]">
                Ethereum
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* pUSD Auto-conversion note */}
        {/* <Box
          sx={{
            mt: 1,
            p: 1.5,
            borderRadius: '16px',
            bgcolor: 'rgba(92, 107, 192, 0.08)',
            border: '1px solid rgba(92, 107, 192, 0.15)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              sx={{ fontSize: '10px', fontWeight: 800, color: '#627EEA', letterSpacing: '0.05em' }}
            >
              AUTO-UPGRADE ACTIVE
            </Typography>
          </Box>
          <Typography
            sx={{ fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.5, fontWeight: 500 }}
          >
            USDC.e is automatically upgraded to <strong>pUSD</strong> for seamless trading.
          </Typography>
        </Box> */}

        <Box className="mt-4 flex min-h-[32px] items-center justify-between border-t border-[var(--border-light)] pt-4">
          {isLoadingQuotes ? (
            <Box className="flex items-center gap-2">
              <Box className="h-3 w-3 animate-spin rounded-full border-2 border-[var(--text-muted)] border-t-transparent" />
              <Typography className="text-[11px] font-medium text-[var(--text-muted)]">
                {t('depositModal.fetchingBestPrice', { defaultValue: 'Fetching best price...' })}
              </Typography>
            </Box>
          ) : displayError ? null : availabilityError ? (
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                color: 'var(--accent-red)',
              }}
            >
              {t('depositModal.noPaymentMethods', {
                defaultValue:
                  'No payment methods available. Please select a different fiat currency',
              })}
            </Typography>
          ) : (
            <Box
              onClick={onShowProviderList}
              className="ml-auto flex cursor-pointer items-center gap-1.5"
            >
              <Typography className="text-[11px] font-medium text-[var(--text-muted)]">
                {t('depositModal.by', { defaultValue: 'By' })}
              </Typography>
              <Box className="flex items-center gap-1">
                <Box className="flex h-4 w-4 overflow-hidden rounded-full border border-[var(--border-color)] bg-[#2A2D35] shadow-sm">
                  {hasSelectedQuote && selectedProviderLogo ? (
                    <Box
                      component="img"
                      src={selectedProviderLogo}
                      className="h-full w-full object-cover"
                    />
                  ) : hasSelectedQuote && selectedProviderName ? (
                    <Box className="flex h-full w-full items-center justify-center text-[8px] font-bold text-white">
                      {selectedProviderName.charAt(0).toUpperCase()}
                    </Box>
                  ) : (
                    <Box className="h-full w-full bg-[#7C3AED]" />
                  )}
                </Box>
                {hasSelectedQuote ? (
                  <Typography className="text-[11px] font-bold text-[var(--text-primary)]">
                    {selectedProviderName}
                  </Typography>
                ) : (
                  <Typography className="text-[11px] font-bold text-[var(--text-primary)]">
                    {t('common.loading', { defaultValue: 'Loading...' })}
                  </Typography>
                )}
                <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'var(--text-muted)' }} />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
